// Copyright (c) 2021 WiseTime. All rights reserved.

import { useDispatch, useSelector } from "react-redux"
import { useCallback } from "react"
import { Destination, ListItemFavouriteType, ListItemType } from "features/shared/search-dropdown/types"
import * as Slice from "features/tags/slice"
import * as Actions from "features/tags/actions"
import * as TimelineActions from "features/timeline/actions"
import * as TimelineSlice from "features/timeline/slices/timeline"
import {
  CreateFavouriteTagRequest,
  DeleteFavouriteTagRequest,
  FavouriteTag,
  GetTagRequest,
  ReorderFavouriteTagRequest,
  SearchTagsRequest,
} from "generated/legebill/site/tag/v4/tag_pb"
import { Tag, TagShallow } from "generated/legebill/type/tag_pb"
import * as UserContextSlice from "features/user-context/slice"
import { Option } from "features/shared/Dropdown"
import {
  AttachTagRequest,
  IdList,
} from "generated/wisetime/user/timeline/v4/timeline_pb"
import { SEARCH_LIST_ITEM_COUNT } from "features/shared/search-dropdown/config"
import { TagRef } from "../../generated/wisetime/user/type/tag_ref_pb"
import { RecentTagsOrderBy } from "features/tags/slice"
import { notUndefined } from "../../lib/utils"

type UseTagsDropdown = {
  onItemSelected?: (tagRef: TagRef) => void,
}
export const useTagsDropdown = ({ onItemSelected }: UseTagsDropdown) => {
  const dispatch = useDispatch()
  const isSearchingTags = useSelector(Slice.selectIsSearching)
  const favouriteTags = useSelector(Slice.selectFavouriteTags)
  const recentTags = useSelector(Slice.selectRecentTags)
  const resultTags = useSelector(Slice.selectResultTagsShallow)
  const tagsSearchOrderBy = useSelector(Slice.selectSearchOrderBy)
  const tagsRecentOrderBy = useSelector(Slice.selectRecentOrderBy)
  const tagsViewMode = useSelector(Slice.selectViewMode)
  const currentSite = useSelector(UserContextSlice.selectCurrentSite)
  const rowsCache = useSelector(TimelineSlice.selectTimeRowsCache)
  const groupsCache = useSelector(TimelineSlice.selectTimeGroupsCache)
  const siteId = currentSite?.id
  const favouriteTagsList = favouriteTags.map(({
    id,
    tag,
    siteListIndex,
  }: FavouriteTag.AsObject): ListItemFavouriteType => ({
    id,
    refId: tag?.id || "",
    label: tag?.name || "",
    description: tag?.description,
    siteListIndex,
  }))
  const prepareList = useCallback((list: Tag.AsObject[] | TagShallow.AsObject[]): ListItemType[] => list.map(({
    id,
    name,
    description,
  }: Tag.AsObject | TagShallow.AsObject): ListItemType => ({
    id,
    label: name,
    description,
  })), [dispatch])
  const recentTagsList = prepareList(recentTags)
  const resultTagsList = prepareList(resultTags)
  const allTags: ListItemType[] = [
    ...favouriteTagsList.map(({ refId, label, description }) => ({
      id: refId,
      label,
      description,
    })),
    ...recentTagsList,
    ...resultTagsList,
  ]

  const onAddTagToDestination =
    useCallback((id: string, timeGroupsIds: Destination, timeRowsIds: Destination): void => {
      if (!currentSite || !siteId) {
        return
      }
      const timeRowsWithoutTag = timeRowsIds
        .map(id => rowsCache[id])
        .filter(notUndefined)
        .filter(timeRow => !timeRow.tagsList.some(tag => tag.id === id))

      const timeRowsWithoutTagIds = timeRowsWithoutTag
        .map(timeRow => timeRow.id)

      const attachTagRequest = new AttachTagRequest().setTagId(id)
      if (timeRowsWithoutTagIds.length > 0) {
        attachTagRequest.setTimeRowIds(new IdList().setIdsList(timeRowsWithoutTagIds))
      }

      const timeGroupsWithoutTag = timeGroupsIds
        .map(id => groupsCache[id])
        .filter(notUndefined)
        .filter(timeGroup => !timeGroup.tagsList.some(tag => tag.id === id))

      const timeGroupsWithoutTagIds = timeGroupsWithoutTag
        .map(timeGroup => timeGroup.id)
      if (timeGroupsWithoutTagIds.length > 0) {
        attachTagRequest.setTimeGroupIds(new IdList().setIdsList(timeGroupsWithoutTagIds))
      }
      const tag = allTags.find(tag => tag.id === id)
      const tagRef = new TagRef().setId(id).setSiteId(siteId)
      if (tag) {
        tagRef.setName(tag.label)
      }
      if (tag?.id) {
        dispatch(Actions.getTag({
          request: new GetTagRequest().setTagId(tag.id)
        }))
      }
      if (onItemSelected) {
        onItemSelected(tagRef)
        return
      }
      dispatch(TimelineActions.attachTag({ attachTagRequest, tag: tagRef }))
    }, [dispatch, siteId, allTags])

  const onSearchTags = useCallback((searchText: string): void => {
    if (!siteId) {
      return
    }
    const request = new SearchTagsRequest()
      .setSiteId(siteId)
      .setSearchText(searchText)
      .setOrderBy(tagsSearchOrderBy)
      .setPageSize(SEARCH_LIST_ITEM_COUNT)
    dispatch(Actions.searchTagsShallow(request))
  }, [dispatch, siteId, tagsSearchOrderBy])

  const onCreateFavouriteTag = useCallback((e, refId: string): void => {
    e.stopPropagation()
    const request = new CreateFavouriteTagRequest().setTagId(refId)
    dispatch(Actions.createFavouriteTag(request))
  }, [dispatch])

  const onDeleteFavouriteTag = useCallback((e, favouriteTagId: string): void => {
    e.stopPropagation()
    if (!siteId) {
      return
    }
    const request = new DeleteFavouriteTagRequest().setFavouriteTagId(favouriteTagId)
    dispatch(Actions.deleteFavouriteTag({
      request,
      siteId,
    }))
  }, [dispatch, siteId])

  const onResetSearchTags = useCallback((): void => {
    dispatch(Slice.resetResultTagsShallow())
  }, [dispatch])

  const onReorderFavouriteTag = useCallback((id: string, newIndex: number): void => {
    if (!siteId) {
      return
    }
    const request = new ReorderFavouriteTagRequest().setFavouriteTagId(id).setSiteListIndex(newIndex)
    dispatch(Actions.reorderFavouriteTag({
      request,
      siteId,
    }))
  }, [dispatch, siteId])
  const onChangeTagsOrderBy = useCallback((value: Option["value"]): void => {
    dispatch(Slice.setSearchOrderBy(value as SearchTagsRequest.OrderBy))
  }, [dispatch])
  const onChangeRecentTagsOrderBy = useCallback((value: Option["value"]): void => {
    dispatch(Slice.setRecentOrderBy(value as RecentTagsOrderBy))
  }, [dispatch])
  const onChangeTagsViewMode = useCallback((value: Slice.ViewMode): void => {
    dispatch(Slice.setViewMode(value))
  }, [dispatch])
  const onFetchTags = useCallback((): void => {
    if (!siteId) {
      return
    }
    dispatch(Actions.fetchRecentTags())
    dispatch(Actions.fetchFavouriteTags({ siteId }))
  }, [dispatch, siteId])

  return {
    isSearchingTags,
    favouriteTagsList,
    recentTagsList,
    resultTagsList,
    tagsSearchOrderBy,
    tagsRecentOrderBy,
    tagsViewMode,
    onAddTagToDestination,
    onSearchTags,
    onResetSearchTags,
    onCreateFavouriteTag,
    onDeleteFavouriteTag,
    onReorderFavouriteTag,
    onChangeTagsOrderBy,
    onChangeRecentTagsOrderBy,
    onChangeTagsViewMode,
    onFetchTags,
  }
}
